const AUDACY_PIXEL_BASE_URL = 'https://trkn.us/pixel/conv'
const AUDACY_PROGRAM_ID = 27088

/** A conversion event type Audacy has assigned us a group id for. Values come from their notes file and are fixed. */
export interface AudacyPixel {
  group: string
  gid: number
  /** Event-specific params Audacy defined, in the order they expect them. */
  params?: ReadonlyArray<readonly [name: string, value: string]>
}

export const AUDACY_SITEWIDE_PIXEL: AudacyPixel = {group: 'sitewide', gid: 71384}

export function audacyPurchasePixel({
  orderId,
  amountTotalInCents,
  currency
}: {
  orderId: string
  amountTotalInCents?: number
  currency: string | null
}): AudacyPixel {
  // Audacy reads the order id from cv1 and the total from rev, which they require as a bare decimal
  // amount — no symbol, no cents. They defined no currency parameter, so a non-USD total would be
  // booked as dollars; recording the conversion without a revenue figure beats recording a wrong
  // one. Dropping it also sidesteps zero-decimal currencies, where Stripe's amount is not in cents.
  const canReportRevenue = currency?.toLowerCase() === 'usd' && amountTotalInCents !== undefined
  const params: Array<readonly [string, string]> = canReportRevenue
    ? [
        ['cv1', orderId],
        ['rev', (amountTotalInCents / 100).toFixed(2)]
      ]
    : [['cv1', orderId]]

  return {group: 'purchase', gid: 71385, params}
}

/**
 * Script content that fires an Audacy pixel, for a Ketch-gated `next/script`.
 *
 * Audacy fixes the URL shape: semicolon-delimited params ending in `ord`, which is their cache
 * buster rather than an order id. Their written procedure replaces `ord` with GTM's own
 * cache-busting parameter, but that step only applies to a GTM install; loading the pixel directly
 * means keeping `ord`, which is how they ship it. The buster mixes a random suffix into the
 * timestamp so two conversions in the same millisecond stay distinct.
 *
 * The per-group flag keeps a conversion from being counted twice when one tree mounts the tag more
 * than once — a layout's `PageMetadata` plus a child's, say — since Next emits an inline
 * `beforeInteractive` script once per mount rather than deduplicating on `id`. Assigning the image
 * to that same flag also keeps a reference to it while the request is in flight.
 */
export function audacyPixelScript(pixel: AudacyPixel): string {
  const params = (pixel.params ?? []).map(([name, value]) => `${name}=${encodeURIComponent(value)};`).join('')
  const urlPrefix = `${AUDACY_PIXEL_BASE_URL}/ppt=${AUDACY_PROGRAM_ID};g=${pixel.group};gid=${pixel.gid};${params}ord=`
  const firedKey = JSON.stringify(`__audacyPixelFired_${pixel.gid}`)
  return (
    `(function(){var k=${firedKey};if(window[k])return;window[k]=new Image(1,1);` +
    `window[k].src=${JSON.stringify(urlPrefix)}+new Date().getTime()+Math.floor(Math.random()*1e6);})();`
  )
}
